import { iconUrl } from '../utils/assets';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');

if (
  typeof window !== 'undefined' &&
  process.env.NODE_ENV === 'production' &&
  API_URL.startsWith('http://') &&
  !/localhost|127\.0\.0\.1/.test(API_URL)
) {
  throw new Error('REACT_APP_API_URL must use HTTPS in production');
}

let memoryToken = null;
let reauthPromise = null;

export function getToken() {
  return memoryToken || sessionStorage.getItem('Auth') || null;
}

export function setToken(token) {
  memoryToken = token || null;
  if (token) {
    // sessionStorage dies with tab — better than localStorage for XSS dwell time
    sessionStorage.setItem('Auth', token);
    try {
      localStorage.removeItem('Auth');
    } catch (_) {
      /* ignore */
    }
  } else {
    sessionStorage.removeItem('Auth');
    try {
      localStorage.removeItem('Auth');
    } catch (_) {
      /* ignore */
    }
  }
}

function extractInitDataLive() {
  try {
    const tg = window.Telegram?.WebApp;
    if (tg?.initData) return tg.initData;
    const hash = String(window.location.hash || '');
    const q = String(window.location.search || '');
    const fromHash = hash.match(/tgWebAppData=([^&]+)/);
    if (fromHash) return decodeURIComponent(fromHash[1]);
    const fromQ = q.match(/[?&]tgWebAppData=([^&]+)/);
    if (fromQ) return decodeURIComponent(fromQ[1]);
  } catch (_) {
    /* ignore */
  }
  return '';
}

async function tryReauth() {
  if (reauthPromise) return reauthPromise;
  reauthPromise = (async () => {
    const initData = extractInitDataLive();
    if (!initData) return false;
    try {
      const auth = await request('/api/auth/auth-initdata/', {
        method: 'POST',
        body: { InitData: initData },
        auth: false,
        _skip401: true,
      });
      if (auth?.access) {
        setToken(auth.access);
        return true;
      }
    } catch (_) {
      /* ignore */
    }
    return false;
  })().finally(() => {
    reauthPromise = null;
  });
  return reauthPromise;
}

export function isSafeTelegramUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return false;
    const host = u.hostname.toLowerCase();
    return host === 't.me' || host === 'telegram.me' || host === 'telegram.dog';
  } catch {
    return false;
  }
}

/** Extract Stars invoice slug from createInvoiceLink result. */
export function invoiceSlugFromLink(url) {
  if (!url || typeof url !== 'string') return '';
  const raw = url.trim();
  // Already a bare slug: $AbCd… or AbCd…
  if (/^\$?[A-Za-z0-9_-]{6,}$/.test(raw) && !raw.includes('://')) {
    return raw.startsWith('$') ? raw.slice(1) : raw;
  }
  try {
    const u = new URL(raw);
    const path = u.pathname || '';
    // https://t.me/$SLUG  or  https://t.me/invoice/SLUG
    const dollar = path.match(/\/\$([A-Za-z0-9_-]+)/);
    if (dollar) return dollar[1];
    const inv = path.match(/\/invoice\/\$?([A-Za-z0-9_-]+)/i);
    if (inv) return inv[1];
  } catch (_) {
    /* ignore */
  }
  return '';
}

/** Telegram Stars invoice links: https://t.me/$… or bare $slug */
export function isSafeInvoiceUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const raw = url.trim();
  if (invoiceSlugFromLink(raw)) return true;
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:') return false;
    const host = u.hostname.toLowerCase();
    if (!(host === 't.me' || host === 'telegram.me' || host === 'telegram.dog')) return false;
    return (
      u.pathname.startsWith('/$') ||
      u.pathname.includes('/invoice') ||
      u.pathname.includes('$') ||
      u.search.includes('start=')
    );
  } catch {
    return false;
  }
}

export function openSafeTelegramLink(url) {
  if (!isSafeTelegramUrl(url)) return false;
  const tg = window.Telegram?.WebApp;
  try {
    tg?.ready?.();
  } catch (_) {
    /* ignore */
  }
  // Prefer in-Telegram navigation (boost / channel links).
  try {
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(url);
      return true;
    }
  } catch (_) {
    /* fall through */
  }
  // Android sometimes only exposes openLink
  try {
    if (tg?.openLink) {
      tg.openLink(url, { try_instant_view: false });
      return true;
    }
  } catch (_) {
    /* fall through */
  }
  // Last resort — still keeps the user in Telegram for t.me links on many builds
  try {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
  } catch (_) {
    /* ignore */
  }
  return false;
}

/**
 * Open Stars invoice via WebApp.openInvoice.
 * Do NOT use openTelegramLink for $ invoices first (Share sheet on some Android).
 * openLink is an acceptable last resort on stubborn Android / forks.
 */
export function openSafeInvoice(url, callback) {
  const tg = window.Telegram?.WebApp;
  if (!tg) return false;
  try {
    tg.ready?.();
  } catch (_) {
    /* ignore */
  }

  const raw = typeof url === 'string' ? url.trim() : '';
  if (!raw || !isSafeInvoiceUrl(raw)) return false;

  const slug = invoiceSlugFromLink(raw);
  const primary = raw.includes('://') ? raw : slug ? `https://t.me/$${slug}` : raw;
  const cb = typeof callback === 'function' ? callback : undefined;

  // Some Android bridges expose openInvoice as a non-function callable host object.
  if (tg.openInvoice) {
    try {
      tg.openInvoice(primary, cb);
      return 'invoice';
    } catch (_) {
      if (slug) {
        try {
          tg.openInvoice(`$${slug}`, cb);
          return 'invoice';
        } catch (__) {
          /* fall through */
        }
      }
    }
  }

  // Last resort: open the invoice URL inside Telegram (no status callback → poll)
  try {
    if (tg.openLink) {
      tg.openLink(primary, { try_instant_view: false });
      return 'link';
    }
  } catch (_) {
    /* ignore */
  }
  return false;
}

export function safeIconSrc(icon) {
  if (!icon || typeof icon !== 'string') return iconUrl('toolbox.png');
  if (!/^[a-zA-Z0-9_-]+\.(png|webp|jpg|jpeg|svg)$/.test(icon)) {
    return iconUrl('toolbox.png');
  }
  return iconUrl(icon);
}

async function request(path, { method = 'GET', body, auth = true, _skip401 = false, keepalive = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    keepalive: Boolean(keepalive),
  });
  let data = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text };
    }
  }
  if (!response.ok) {
    if (response.status === 401 && auth && !_skip401) {
      const ok = await tryReauth();
      if (ok) {
        return request(path, { method, body, auth, _skip401: true });
      }
    }
    if (data?.blocked && data?.security) {
      try {
        window.dispatchEvent(new CustomEvent('tapmars:security', { detail: data.security }));
      } catch (_) {
        /* ignore */
      }
    }
    let message = `HTTP ${response.status}`;
    const detail = data && data.detail;
    if (typeof detail === 'string') {
      // Never surface Django HTML error pages in the UI
      if (/<!doctype|<html/i.test(detail)) {
        message =
          response.status >= 500
            ? 'Ошибка сервера. Закрой и открой игру снова.'
            : 'Ошибка запроса. Попробуй ещё раз.';
      } else {
        message = detail;
      }
    } else if (Array.isArray(detail)) message = detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
    else if (detail && typeof detail === 'object') message = JSON.stringify(detail);
    else if (data && data.message) message = data.message;
    else if (response.status >= 500) message = 'Ошибка сервера. Закрой и открой игру снова.';
    const err = new Error(message);
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  authInitData: async (initData) => {
    try {
      return await request('/api/auth/auth-initdata/', {
        method: 'POST',
        body: { InitData: initData },
        auth: false,
      });
    } catch (err) {
      // Banned accounts: 403 with security payload, no JWT
      if (err?.status === 403 && err?.data?.blocked) return err.data;
      throw err;
    }
  },
  meTelegram: () => request('/api/auth/me-telegram/', { method: 'POST' }),
  setLanguage: (language) =>
    request('/api/auth/language/', { method: 'POST', body: { language } }),
  clickerSync: () => request('/api/clicker/sync/', { method: 'POST' }),
  clickerTap: (payload, opts = {}) =>
    request('/api/clicker/tap/', { method: 'POST', body: payload, keepalive: opts.keepalive }),
  upgradesList: () => request('/api/upgrades/'),
  upgradesBuy: (upgradeId) => request('/api/upgrades/buy/', { method: 'POST', body: { upgrade_id: upgradeId } }),
  tasksList: () => request('/api/tasks/'),
  tasksClaim: (taskId) => request('/api/tasks/claim/', { method: 'POST', body: { task_id: taskId } }),
  tasksCheck: (taskId, extra = {}) =>
    request('/api/tasks/check/', { method: 'POST', body: { task_id: taskId, ...extra } }),
  referrals: () => request('/api/referrals/'),
  leaderboard: () => request('/api/leaderboard/'),
  seasons: () => request('/api/seasons/'),
  paymentProducts: () => request('/api/payments/products/'),
  createInvoice: (sku, opts = {}) =>
    request('/api/payments/create-invoice/', {
      method: 'POST',
      body: {
        sku,
        deliver_chat: Boolean(opts.deliver_chat || opts.deliverChat),
      },
    }),
  wallet: () => request('/api/economy/wallet/'),
  cosmetics: () => request('/api/cosmetics/'),
  cosmeticsBuy: (id) => request('/api/cosmetics/buy/', { method: 'POST', body: { cosmetic_id: id } }),
  cosmeticsEquip: (id) => request('/api/cosmetics/equip/', { method: 'POST', body: { cosmetic_id: id } }),
  cosmeticsUnequip: (id) => request('/api/cosmetics/unequip/', { method: 'POST', body: { cosmetic_id: id } }),
  prestige: () => request('/api/progression/prestige/'),
  doPrestige: () => request('/api/progression/prestige/', { method: 'POST', body: {} }),
  streak: () => request('/api/progression/streak/'),
  clans: () => request('/api/clans/'),
  clanCreate: (payload) => request('/api/clans/create/', { method: 'POST', body: payload }),
  clanJoin: (clanId) => request('/api/clans/join/', { method: 'POST', body: { clan_id: clanId } }),
  clanJoinCode: (code) =>
    request('/api/clans/join/', { method: 'POST', body: { invite_code: code } }),
  clanLeave: () => request('/api/clans/leave/', { method: 'POST', body: {} }),
  clanPrivacy: (isOpen, extra = {}) =>
    request('/api/clans/privacy/', {
      method: 'POST',
      body: { is_open: isOpen, ...extra },
    }),
  clanSetDescription: (description) =>
    request('/api/clans/privacy/', { method: 'POST', body: { description } }),
  clanUpgrade: (kind) => request('/api/clans/upgrade/', { method: 'POST', body: { kind } }),
  pvpList: () => request('/api/pvp/'),
  pvpCreate: (payload) => request('/api/pvp/create/', { method: 'POST', body: payload }),
  pvpAccept: (id) => request('/api/pvp/accept/', { method: 'POST', body: { challenge_id: id } }),
  pvpCancel: (id) => request('/api/pvp/cancel/', { method: 'POST', body: { challenge_id: id } }),
  // Дуэли
  duelState: () => request('/api/pvp/state/'),
  duelMatchmake: (payload) => request('/api/pvp/matchmake/', { method: 'POST', body: payload }),
  duelTake: (id) => request('/api/pvp/take/', { method: 'POST', body: { challenge_id: id } }),
  duelDecline: (id) => request('/api/pvp/decline/', { method: 'POST', body: { challenge_id: id } }),
  duelRematch: (id) => request('/api/pvp/rematch/', { method: 'POST', body: { challenge_id: id } }),
  duelSeen: (id) => request('/api/pvp/seen/', { method: 'POST', body: { challenge_id: id } }),
  duelTop: () => request('/api/pvp/top/'),
  battlePass: () => request('/api/battlepass/'),
  battlePassClaim: (level, track) =>
    request('/api/battlepass/claim/', { method: 'POST', body: { level, track } }),
  battlePassClaimAll: () =>
    request('/api/battlepass/claim/', { method: 'POST', body: { claim_all: true } }),
  battlePassDaily: () => request('/api/battlepass/daily/', { method: 'POST', body: {} }),
  securityStatus: () => request('/api/security/status/'),
  engagement: () => request('/api/engagement/'),
  claimDaily: () => request('/api/engagement/daily/', { method: 'POST', body: {} }),
  claimOffline: () => request('/api/engagement/offline/', { method: 'POST', body: {} }),
  claimOffer: (code) => request('/api/engagement/offer/', { method: 'POST', body: { code } }),
  sendGift: (friendId, giftType = 'energy') =>
    request('/api/engagement/gift/', { method: 'POST', body: { friend_id: friendId, gift_type: giftType } }),
  setOnboarding: (payload) => request('/api/engagement/onboarding/', { method: 'POST', body: payload }),
  claimShake: () => request('/api/engagement/shake/', { method: 'POST', body: {} }),
  clanTransfer: (userId) => request('/api/clans/transfer/', { method: 'POST', body: { user_id: userId } }),
  clanKick: (userId) => request('/api/clans/kick/', { method: 'POST', body: { user_id: userId } }),
  clanDisband: () => request('/api/clans/disband/', { method: 'POST', body: {} }),
};

export default api;
